import { useEffect, useState } from "react";
import { api } from "@/App";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Palette, Save, RotateCcw, ExternalLink, ChevronDown, ChevronUp, Key } from "lucide-react";
import BrandLogo from "@/components/BrandLogo";

const PRESET_COLORS = ["#6366F1", "#10B981", "#F59E0B", "#F43F5E", "#06B6D4", "#8B5CF6", "#EC4899", "#22D3EE"];

export default function SettingsDialog({ open, onOpenChange, config, refreshConfig }) {
  const [name, setName] = useState("");
  const [tagline, setTagline] = useState("");
  const [disclaimer, setDisclaimer] = useState("");
  const [color, setColor] = useState("#6366F1");
  const [saving, setSaving] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [newKey, setNewKey] = useState("");

  useEffect(() => {
    if (!open) return;
    const b = config?.brand || {};
    setName(b.name || "");
    setTagline(b.tagline || "");
    setDisclaimer(b.disclaimer || "");
    setColor(b.color || "#6366F1");
    setNewKey("");
    setShowAdvanced(false);
  }, [open, config?.brand]);

  const saveBrand = async () => {
    setSaving(true);
    try {
      await api.put("/config/brand", { name: name.trim(), tagline: tagline.trim(), disclaimer: disclaimer.trim(), color });
      toast.success("Brand updated");
      refreshConfig();
      onOpenChange(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    setSaving(false);
  };

  const resetBrand = async () => {
    setSaving(true);
    try {
      const { data } = await api.delete("/config/brand");
      setName(data.brand.name);
      setTagline(data.brand.tagline);
      setDisclaimer(data.brand.disclaimer);
      setColor(data.brand.color);
      toast.success("Reverted to default brand");
      refreshConfig();
    } catch (e) { toast.error("Reset failed"); }
    setSaving(false);
  };

  const saveKey = async () => {
    if (!newKey.trim()) return;
    try {
      const { data } = await api.put("/config/alpha-key", { key: newKey.trim() });
      toast.success(`Fallback key saved (${data.masked})`);
      refreshConfig();
      setNewKey("");
    } catch (e) { toast.error("Key save failed"); }
  };

  const resetKey = async () => {
    try {
      await api.delete("/config/alpha-key");
      toast.success("Reverted to default key");
      refreshConfig();
    } catch (e) { toast.error("Reset failed"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#121824] border-slate-800 text-slate-100 max-w-md max-h-[90vh] overflow-y-auto" data-testid="settings-dialog">
        <DialogHeader>
          <DialogTitle className="font-head text-xl flex items-center gap-2">
            <Palette className="w-4 h-4" style={{ color }} /> Brand Settings
          </DialogTitle>
          <DialogDescription className="text-slate-500 text-xs">
            Customise the name, tagline, disclaimer and accent colour used across the app and in every email.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Logo preview */}
          <div className="rounded-xl border border-slate-800 bg-[#0B0F17] p-4 flex items-center gap-3" data-testid="brand-preview">
            <div
              className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 border"
              style={{ background: `${color}18`, borderColor: `${color}55` }}
            >
              <BrandLogo color={color} size={34} />
            </div>
            <div className="min-w-0">
              <div className="font-head font-extrabold text-lg leading-tight truncate">{name || "LaukStocks"}</div>
              <div className="font-mono text-[10px] text-slate-500 uppercase tracking-widest truncate">{tagline || "Preview"}</div>
            </div>
          </div>

          <div>
            <Label className="text-[10px] uppercase tracking-wider text-slate-500">Brand name</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="LaukStocks"
              className="bg-[#0B0F17] border-slate-800 font-head text-base mt-1"
              data-testid="brand-name-input"
              maxLength={40}
            />
          </div>

          <div>
            <Label className="text-[10px] uppercase tracking-wider text-slate-500">Tagline</Label>
            <Input
              value={tagline}
              onChange={(e) => setTagline(e.target.value)}
              placeholder="Stock analyzer helping you make more informed decisions"
              className="bg-[#0B0F17] border-slate-800 text-sm mt-1"
              data-testid="brand-tagline-input"
              maxLength={120}
            />
          </div>

          <div>
            <Label className="text-[10px] uppercase tracking-wider text-slate-500">Accent colour</Label>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              {PRESET_COLORS.map(c => (
                <button
                  key={c}
                  onClick={() => setColor(c)}
                  className={`w-8 h-8 rounded-lg border-2 transition-transform hover:scale-110 ${color === c ? "ring-2 ring-white/50 ring-offset-2 ring-offset-[#121824]" : "border-slate-800"}`}
                  style={{ backgroundColor: c, borderColor: color === c ? c : undefined }}
                  data-testid={`color-${c}`}
                  aria-label={`Pick ${c}`}
                />
              ))}
              <Input
                type="text"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                className="bg-[#0B0F17] border-slate-800 font-mono text-xs h-8 w-24"
                data-testid="brand-color-input"
              />
            </div>
          </div>

          <div>
            <Label className="text-[10px] uppercase tracking-wider text-slate-500">Disclaimer (shown in app footer + every email)</Label>
            <Textarea
              value={disclaimer}
              onChange={(e) => setDisclaimer(e.target.value)}
              placeholder="This is not financial advice..."
              className="bg-[#0B0F17] border-slate-800 text-xs min-h-[72px] mt-1 font-mono"
              data-testid="brand-disclaimer-input"
              maxLength={400}
            />
          </div>

          <div className="flex gap-2">
            <Button
              onClick={resetBrand}
              variant="secondary"
              className="flex-1 bg-slate-800 hover:bg-slate-700"
              disabled={saving}
              data-testid="reset-brand-btn"
            >
              <RotateCcw className="w-3.5 h-3.5 mr-1" /> Reset
            </Button>
            <Button
              onClick={saveBrand}
              disabled={!name.trim() || saving}
              className="flex-1 text-white hover:opacity-90"
              style={{ backgroundColor: color }}
              data-testid="save-brand-btn"
            >
              <Save className="w-3.5 h-3.5 mr-1" /> Save brand
            </Button>
          </div>

          {/* Advanced */}
          <button
            onClick={() => setShowAdvanced(v => !v)}
            className="w-full text-left text-[11px] text-slate-500 font-mono uppercase tracking-widest flex items-center justify-between pt-2 border-t border-slate-800 hover:text-slate-300"
            data-testid="toggle-advanced"
          >
            <span>Advanced · Market data key</span>
            {showAdvanced ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>

          {showAdvanced && (
            <div className="space-y-3 pt-1">
              <div className="rounded-lg border border-slate-800 bg-[#0B0F17] p-3">
                <div className="font-mono text-[10px] text-slate-500 uppercase tracking-widest mb-1">Primary data source</div>
                <div className="font-mono text-sm text-emerald-400">Yahoo Finance · unlimited · free</div>
                <div className="font-mono text-[10px] text-slate-500 mt-2">Fallback Alpha Vantage key (optional)</div>
                <div className="font-mono text-sm text-slate-200" data-testid="current-key-masked">
                  {config?.alpha_key_masked || "— not set —"}
                </div>
                <a
                  href="https://www.alphavantage.co/support/#api-key"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-2 inline-flex items-center gap-1 text-[11px] text-indigo-400 hover:text-indigo-300 font-mono"
                >
                  Get a free key <ExternalLink className="w-3 h-3" />
                </a>
              </div>
              <Input
                value={newKey}
                onChange={(e) => setNewKey(e.target.value)}
                placeholder="Paste new fallback key"
                className="bg-[#0B0F17] border-slate-800 font-mono text-sm"
                data-testid="alpha-key-input"
              />
              <div className="flex gap-2">
                <Button onClick={resetKey} variant="secondary" size="sm" className="flex-1 bg-slate-800 hover:bg-slate-700" data-testid="reset-key-btn">
                  <RotateCcw className="w-3 h-3 mr-1" /> Reset
                </Button>
                <Button onClick={saveKey} size="sm" disabled={!newKey.trim()} className="flex-1 bg-indigo-500 hover:bg-indigo-600" data-testid="save-key-btn">
                  <Key className="w-3 h-3 mr-1" /> Save key
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
